import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API_URL } from 'src/assets/http/apis';
import { ISalesContactRequest, ISalesContactResponse } from '../interfaces';

/**
 * Client for the sales-contact-request endpoint (MVP-9).
 *
 * On 201 the server has upserted the user's billing snapshot, recorded a
 * `SalesContactRequest` row, and dispatched the sales notification email.
 * On 502 (`salesContact.email_failed`) the server-side insert is rolled
 * back — the caller should surface a retry-able error toast and keep the
 * dialog open so the user can click submit again once ACS is reachable.
 */
@Injectable({
  providedIn: 'root',
})
export class SalesContactService {
  constructor(private http: HttpClient) {}

  /**
   * @description Submits a sales-contact request.
   * @param {ISalesContactRequest} dto - Billing snapshot + optional message.
   * @returns {Observable<ISalesContactResponse>} Emits `{ id }` on 201.
   */
  submit(dto: ISalesContactRequest): Observable<ISalesContactResponse> {
    const url = `${environment.apiUrl}${API_URL.SALES.CONTACT_REQUESTS}`;
    return this.http.post<ISalesContactResponse>(url, dto);
  }

  /**
   * Returns the caller's most recent unresolved sales-contact request
   * (status = New / InProgress / Handled), or null. Used by the renewal
   * banner to flip its CTA to a "request pending" badge.
   */
  getMine(): Observable<IMySalesContactRequest | null> {
    const url = `${environment.apiUrl}${API_URL.SALES.CONTACT_REQUESTS_MINE}`;
    return this.http.get<IMySalesContactRequest | null>(url);
  }
}

export interface IMySalesContactRequest {
  id: string;
  status: 'New' | 'InProgress' | 'Handled' | 'Closed' | 'Approved' | 'Denied';
  submittedAt: string;
  message?: string | null;
}
